import { useState } from "react";
import { CheckCircle2, Clock, Plus, X } from "lucide-react";
import { membershipApi } from "../api";
import { MEMBERSHIP_PERMISSIONS } from "../permissions";
import type { TradeFollowUp } from "../types";
import { MembershipAccess } from "./MembershipAccess";

const PAGE_SIZE = 5;

const inp =
  "w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100";

function fmtDate(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return v; }
}

interface FollowUpForm {
  followUpDate: string;
  officerName: string;
  remarks: string;
  nextAction: string;
  completed: boolean;
}

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = (): FollowUpForm => ({
  followUpDate: today(),
  officerName: "",
  remarks: "",
  nextAction: "",
  completed: false,
});

export function MembershipTradeFollowUpPanel({
  memberId,
  followUps,
  onReload,
}: {
  memberId: number;
  followUps: TradeFollowUp[];
  onReload: () => void;
}) {
  const [page, setPage] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FollowUpForm>(EMPTY());
  const [saving, setSaving] = useState(false);

  const totalPages = Math.ceil(followUps.length / PAGE_SIZE) || 1;
  const visible = followUps.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const set = <K extends keyof FollowUpForm>(k: K, v: FollowUpForm[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await membershipApi.addTradeFollowUp(memberId, form);
      setForm(EMPTY());
      setShowForm(false);
      onReload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-black text-slate-900">Trade Follow-ups</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            {followUps.filter((f) => !f.completed).length} open · {followUps.length} total
          </p>
        </div>
        <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.TRADE_UPDATE]}>
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-2xl bg-emerald-700 px-3 py-2 text-xs font-black text-white hover:bg-emerald-800"
          >
            {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {showForm ? "Cancel" : "Log Follow-up"}
          </button>
        </MembershipAccess>
      </div>

      {showForm && (
        <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="mb-3 text-xs font-black uppercase tracking-wide text-emerald-700">New Follow-up</p>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="label">Follow-up Date</label>
              <input type="date" value={form.followUpDate} onChange={(e) => set("followUpDate", e.target.value)} className={inp} />
            </div>
            <div>
              <label className="label">Officer Name</label>
              <input value={form.officerName} onChange={(e) => set("officerName", e.target.value)} placeholder="Your name" className={inp} />
            </div>
            <div className="md:col-span-2">
              <label className="label">Remarks</label>
              <textarea value={form.remarks} onChange={(e) => set("remarks", e.target.value)} rows={3} placeholder="What was discussed or actioned…" className={inp} />
            </div>
            <div className="md:col-span-2">
              <label className="label">Next Action</label>
              <input value={form.nextAction} onChange={(e) => set("nextAction", e.target.value)} placeholder="e.g. Send fee invoice by end of week" className={inp} />
            </div>
            <div className="md:col-span-2">
              <label className="flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
                <input type="checkbox" checked={form.completed} onChange={(e) => set("completed", e.target.checked)} className="accent-emerald-700" />
                <span className="text-sm font-bold text-slate-700">Mark as completed</span>
              </label>
            </div>
          </div>
          <button type="button" onClick={save} disabled={saving} className="mt-4 rounded-2xl bg-emerald-700 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-800 disabled:opacity-60">
            {saving ? "Saving…" : "Save Follow-up"}
          </button>
        </div>
      )}

      {followUps.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400">No follow-ups yet.</div>
      ) : (
        <>
          <div className="mt-4 grid gap-3">
            {visible.map((f) => (
              <div key={f.id} className={`rounded-2xl border p-4 ${f.completed ? "border-slate-200 bg-slate-50 opacity-70" : "border-amber-200 bg-amber-50"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    {f.completed
                      ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                      : <Clock className="h-4 w-4 shrink-0 text-amber-600" />
                    }
                    <span className="text-sm font-black text-slate-800">
                      {fmtDate(f.followUpDate)}
                      {f.officerName && <span className="ml-2 text-xs font-bold text-slate-500">· {f.officerName}</span>}
                    </span>
                  </div>
                  {f.completed && (
                    <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700">Done</span>
                  )}
                </div>
                {f.remarks && <p className="mt-2 text-sm text-slate-700">{f.remarks}</p>}
                {f.nextAction && (
                  <p className="mt-1.5 text-xs text-slate-500">
                    <span className="font-black">Next:</span> {f.nextAction}
                  </p>
                )}
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs text-slate-400">{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, followUps.length)} of {followUps.length}</span>
              <div className="flex gap-2">
                <button type="button" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-bold disabled:opacity-40">Prev</button>
                <button type="button" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)} className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-bold disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
